// frontend/modules/drafts/stores/drafts-store.ts
import { create } from 'zustand';
import { DraftProcess, DraftProcessStatus } from '../types';

interface DraftsState {
  // State
  draftProcesses: DraftProcess[];
  filteredDraftProcesses: DraftProcess[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  selectedStatus: DraftProcessStatus | null;
  activeDraftProcess: DraftProcess | null;

  // Actions
  setDraftProcesses: (draftProcesses: DraftProcess[]) => void;
  setFilteredDraftProcesses: (draftProcesses: DraftProcess[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchTerm: (term: string) => void;
  setSelectedStatus: (status: DraftProcessStatus | null) => void;
  setActiveDraftProcess: (draftProcess: DraftProcess | null) => void;
  filterDraftProcesses: () => void;
}

export const useDraftsStore = create<DraftsState>((set, get) => ({
  draftProcesses: [],
  filteredDraftProcesses: [],
  isLoading: false,
  error: null,
  searchTerm: '',
  selectedStatus: null,
  activeDraftProcess: null,

  setDraftProcesses: (draftProcesses) => {
    // Ensure draftProcesses is an array before setting it
    const validDraftProcesses = Array.isArray(draftProcesses) ? draftProcesses : [];
    set({ draftProcesses: validDraftProcesses });
    get().filterDraftProcesses();
  },
  
  setFilteredDraftProcesses: (filteredDraftProcesses) => set({ filteredDraftProcesses }),
  
  setIsLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error }),
  
  setSearchTerm: (searchTerm) => {
    set({ searchTerm });
    get().filterDraftProcesses();
  },
  
  setSelectedStatus: (selectedStatus) => {
    set({ selectedStatus });
    get().filterDraftProcesses();
  },
  
  setActiveDraftProcess: (activeDraftProcess) => set({ activeDraftProcess }),
  
  filterDraftProcesses: () => {
    const { draftProcesses, searchTerm, selectedStatus } = get();
    
    // Ensure draftProcesses is an array before filtering
    if (!Array.isArray(draftProcesses)) {
      set({ filteredDraftProcesses: [] });
      return;
    }
    
    let filtered = [...draftProcesses];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(draft => 
        draft.name.toLowerCase().includes(term) ||
        draft.processType.toLowerCase().includes(term)
      );
    }
    
    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(draft => draft.status === selectedStatus);
    }
    
    set({ filteredDraftProcesses: filtered });
  }
}));
